import { clsx } from "clsx";
import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { ConditionalTooltip } from "../Tooltip/Tooltip";

type IconButtonSize = "sm" | "md";

// `aria-label` is required: icon-only buttons have no visible text, so
// screen-reader users rely on it. Overrides the optional aria-label
// inherited from ButtonHTMLAttributes so callers can't forget it.
export type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  "aria-label": string;
  size?: IconButtonSize;
  tooltipContent?: React.ReactNode;
};

const sizeStyles: Record<IconButtonSize, string> = {
  sm: "h-7 w-7",
  md: "h-9 w-9",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      size = "md",
      type = "button",
      tooltipContent,
      children,
      ...rest
    },
    ref,
  ) => {
    const button = (
      <button
        ref={ref}
        type={type}
        {...rest}
        className={twMerge(
          clsx(
            "flex shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors not-disabled:cursor-pointer not-disabled:hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:cursor-not-allowed disabled:opacity-40",
            sizeStyles[size],
          ),
          className,
        )}
      >
        {children}
      </button>
    );

    return (
      <ConditionalTooltip condition={!!tooltipContent} content={tooltipContent}>
        {button}
      </ConditionalTooltip>
    );
  },
);

IconButton.displayName = "IconButton";
